import React, { useEffect, useState } from 'react'

import config from 'config/config'
import { Spinner } from 'common/components/Spinner/Spinner'
import { useAlert } from 'common/components/Alert/hooks/useAlert'
import { useSetStripeConnectPendingStateMutation } from 'modules/ProductInfo/productInfoApi'
import {
  buildStripeConnectState,
  buildStripeConnectUrl,
  createStripeConnectNonce,
  getStripeConnectRedirectUri,
} from 'modules/ProductInfo/utils/stripeConnect'

export const CONNECT_STRIPE_BUTTON_LABEL = 'Connect with Stripe'
export const CONNECT_STRIPE_NOT_CONFIGURED_TEXT =
  'Stripe Connect is not configured for this environment yet. Please contact our support team.'

/**
 * The self-service entry point for an org that has no Stripe account attached.
 *
 * Clicking the button writes a fresh one-time nonce under the org, then sends
 * the coach to Stripe's OAuth authorize page with that nonce (prefixed by the
 * org id) as `state` and this dashboard's /sell/setup as the redirect URI.
 * Stripe sends the coach back there with `code` and `state`; the setup page
 * hands both to the `exchangeStripeConnect` callable, which verifies the
 * nonce, exchanges the code, and writes the account id and publishable key
 * into `productInfo`. That unlocks the rest of Sell through its live listener.
 */
export function ConnectStripe({
  coachOrgId,
  email,
  stripeConnectConfig = config.stripeConnectConfig,
  navigateTo = (url) => window.location.assign(url),
}) {
  const { createAlert } = useAlert()
  const [isRedirecting, setIsRedirecting] = useState(false)
  const [setStripeConnectPendingState] = useSetStripeConnectPendingStateMutation()

  // After the redirect to Stripe, pressing the browser's Back button usually
  // restores this page from the back-forward cache instead of reloading it, so
  // `isRedirecting` is still true and the button would stay disabled with its
  // spinner forever. Browsers announce that kind of restore with a `pageshow`
  // event whose `persisted` flag is true, which is the moment to reset.
  useEffect(() => {
    const resetAfterBack = (event) => {
      if (event.persisted) setIsRedirecting(false)
    }
    window.addEventListener('pageshow', resetAfterBack)
    return () => window.removeEventListener('pageshow', resetAfterBack)
  }, [])

  const { clientId } = stripeConnectConfig || {}
  const isConfigured = Boolean(clientId)
  const redirectUri = getStripeConnectRedirectUri({ origin: window.location.origin })

  const connect = async () => {
    setIsRedirecting(true)
    try {
      const nonce = createStripeConnectNonce()
      const { error } = (await setStripeConnectPendingState({ coachOrgId, nonce })) || {}
      if (error) {
        throw error
      }
      const state = buildStripeConnectState({ coachOrgId, nonce })
      navigateTo(buildStripeConnectUrl({ clientId, state, redirectUri, email }))
    } catch (err) {
      console.error(err)
      createAlert({ text: 'Could not start the Stripe connection. Please try again.', type: 'danger' })
      setIsRedirecting(false)
    }
  }

  return (
    <div className='flex flex-col items-start'>
      <h2 className='font-bold text-3xl text-tBlack mb-4'>Connect your Stripe account</h2>
      <p className='text-gray-500 mb-6'>
        Payments for your app go straight into your own Stripe account. Connect an existing account or create a new one,
        then come back here to set up your product and pricing.
      </p>
      {isConfigured ? (
        <button
          type='button'
          className='flex items-center px-6 py-3 rounded-lg bg-tGreen text-white font-semibold disabled:opacity-50'
          onClick={connect}
          disabled={!coachOrgId || isRedirecting}
          data-testid='connect-stripe-button'
        >
          {isRedirecting && <Spinner className='w-4 h-4 mr-2' />}
          {CONNECT_STRIPE_BUTTON_LABEL}
        </button>
      ) : (
        <p className='text-tRed'>{CONNECT_STRIPE_NOT_CONFIGURED_TEXT}</p>
      )}
    </div>
  )
}
